import { useMutation } from '@apollo/client';
import { emptyTranslation } from '@assets/i18n/config';
import { graphql } from '@generated/gql';
import {
  AccountTypes,
  ExternalSourceTypes,
  SourceTypes,
  SubAccountInactiveReasons,
} from '@generated/graphql';
import { OrganizationUserAccessTypes } from '@interfaces/organizationUser';
import type { RepCode } from '@interfaces/repCode';
import { RepCodeSelect } from '@ovComponents/3-pattern/repCodeSelect/repCodeSelect';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { delay } from '@utils/common';
import { Box, Typography } from 'hephaestus/web';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CREATE_ACCOUNT = graphql(`
  mutation createAccount($input: CreateAccountInput!) {
    createAccount(input: $input) {
      account {
        id
        type
      }
    }
  }
`);

const CREATE_SUB_ACCOUNT = graphql(`
  mutation createSubAccount($input: CreateSubAccountInput!) {
    createSubAccount(input: $input) {
      subAccount {
        id
      }
    }
  }
`);

const UPDATE_ACCOUNT = graphql(`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
      }
    }
  }
`);

const CLOSE_ACCOUNT = graphql(`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`);

export const NetWorthSelectType = ({ onNext }: { onNext: () => void }) => {
  const { t } = useTranslation(['netWorth', 'workflowCompletions']);
  const { netWorthData, setNetWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { userContext } = useContext(UserContext);

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const setApplicationContext =
    Object.keys(netWorthData).length !== 0 ? setNetWorthData : setWorkflowData;

  const showRepCodeSelect =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE;
  const [selectedRepCode, setSelectedRepCode] = useState<RepCode | undefined>(
    undefined,
  );
  const [repCodeError, setRepCodeError] = useState<boolean>(false);

  const [createAccount, { loading: createAccountLoading }] =
    useMutation(CREATE_ACCOUNT);
  const [createSubAccount, { loading: createSubAccountLoading }] =
    useMutation(CREATE_SUB_ACCOUNT);
  const [updateAccount, { loading: updateAccountLoading }] =
    useMutation(UPDATE_ACCOUNT);
  const [closeAccount] = useMutation(CLOSE_ACCOUNT);

  const onContinue = (newContext: any) => {
    setApplicationContext(newContext);
    // Use setTimeout to ensure state update completes before calling onNext
    setTimeout(() => onNext(), 0);
  };

  const handleChange = async (e: any) => {
    if (createAccountLoading || createSubAccountLoading || updateAccountLoading)
      return;

    if (showRepCodeSelect && !selectedRepCode?.code) {
      setRepCodeError(true);
      return;
    }
    setRepCodeError(false);

    const externalSourceType = e.target.value;

    if (applicableData.currentSubAccountId && applicableData.currentAccountId) {
      const { data: updateAccountData } = await updateAccount({
        variables: {
          input: {
            accountId: applicableData.currentAccountId,
            externalSourceType,
            ...(showRepCodeSelect &&
              selectedRepCode?.code && { repCode: selectedRepCode.code }),
          },
        },
      });

      if (updateAccountData?.updateAccount?.account) {
        const newContext = {
          ...applicableData,
          netWorthData: {
            ...applicableData.netWorthData,
            netWorthAccountNickname: emptyTranslation,
            netWorthAmountCents: 0,
            externalSourceType,
            netWorthStep: 2,
            netWorthCurrency: undefined,
          },
        };
        onContinue(newContext);
      }
    } else {
      const { data: accountData } = await createAccount({
        variables: {
          input: {
            userId: applicableData.objectId || applicableData.currentUserId,
            type: AccountTypes.External,
            externalSourceType,
            sourceType: SourceTypes.External,
            ...(showRepCodeSelect &&
              selectedRepCode?.code && { repCode: selectedRepCode.code }),
          },
        },
      });

      if (accountData?.createAccount?.account) {
        const { account } = accountData.createAccount;
        await delay(500);
        const { data: subAccountData } = await createSubAccount({
          variables: {
            input: {
              accountId: account.id,
              accountType: AccountTypes.External,
              userId: applicableData.objectId || applicableData.currentUserId,
            },
          },
          onError: () =>
            closeAccount({
              variables: {
                input: {
                  accountId: account.id,
                  inactiveReason: SubAccountInactiveReasons.Other,
                },
              },
            }),
        });

        if (subAccountData?.createSubAccount?.subAccount) {
          const { subAccount } = subAccountData.createSubAccount;
          const newContext = {
            ...applicableData,
            currentSubAccountId: subAccount.id,
            currentAccountId: account.id,
            netWorthData: {
              ...applicableData.netWorthData,
              netWorthStep: 2,
              externalSourceType,
            },
          };
          onContinue(newContext);
        }
      }
    }
  };

  const ASSETS = [
    ExternalSourceTypes.Cash,
    ExternalSourceTypes.ExternalInvestmentAccount,
    ExternalSourceTypes.RealEstate,
    ExternalSourceTypes.Vehicles,
    ExternalSourceTypes.Valuables,
    ExternalSourceTypes.OtherAsset,
  ];
  const LIABILITIES = [
    ExternalSourceTypes.CreditCard,
    ExternalSourceTypes.Mortgage,
    ExternalSourceTypes.Loans,
    ExternalSourceTypes.OtherLiability,
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 3.5,
        }}
      >
        <Typography variant='displayLarge' sx={{ fontWeight: 400 }}>
          {t('addNetWorthManually.title')}
        </Typography>
      </Box>
      {showRepCodeSelect && (
        <RepCodeSelect
          repCode={selectedRepCode}
          setRepCode={(repCode) => {
            setSelectedRepCode(repCode);
            if (repCode) setRepCodeError(false);
          }}
          label={t('workflowCompletions:createAccount.repCodeLabel')}
          testId='net-worth-select-type-rep-code'
          error={repCodeError}
          errorText={
            repCodeError
              ? t('workflowCompletions:createAccount.repCodeRequired')
              : undefined
          }
          fullWidth
          sx={{ mb: 3 }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant='titleLarge'>{t('assets')}</Typography>
        {ASSETS.map((asset) => (
          <SelectionTile
            value={asset}
            options={[
              {
                label: t(`categories.${asset}`),
                value: asset,
              },
            ]}
            type='completion'
            onChange={(e: any) => handleChange(e)}
            innerSx={{ minHeight: undefined }}
            sx={{
              paddingY: 1.5,
              paddingLeft: 2.5,
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='titleLarge'>{t('liabilities')}</Typography>
        {LIABILITIES.map((liability) => (
          <SelectionTile
            value={liability}
            options={[
              {
                label: t(`categories.${liability}`),
                value: liability,
              },
            ]}
            type='completion'
            onChange={(e: any) => handleChange(e)}
            innerSx={{ minHeight: undefined }}
            sx={{
              paddingY: 1.5,
              paddingLeft: 2.5,
            }}
          />
        ))}
      </Box>
    </>
  );
};
